import type { GQL_ID } from '@@partner:types:graphql'
import type { CrudFormProps, ProductAttributeFormState } from '@@partner:types:app'

import React, { useCallback, useEffect } from 'react'

import { useFetchSingleProductAttribute } from '@@partner:graphql/query/productAttribute/singleProductAttribute.gql'
import { useMutateSaveProductAttribute } from '@@partner:graphql/mutation/productAttribute/saveProductAttribute.gql'

import { mergeLeft, mergeLeftOnly } from 'merge-left-utils'
import {
  makeProductAttributeFormState,
  mapStateToApiVariable
} from './ProductAttributeFormState'

import { Form, Panel, Grid, Row, Col } from 'rsuite'

import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import FormCheckbox from '@components/common/form/FormCheckbox'
import { SaveButton } from '../../common/form/Buttons'
import ProductAttributeTypePicker from './form/ProductAttributeTypePicker'

import { productAttributeFormValidationModel } from './validation/ProductAttributeFormValidationModel'

import { commonFieldChange } from '@@partner:helpers/util'
import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'
import {
  makeGraphQLErrorNotification,
  notifyOk,
  notifyError
} from '@@partner:helpers/notification'


export interface ProductAttributeProps extends CrudFormProps {
  attributeId?: GQL_ID
}

export default function ProductAttributeForm ({
  attributeId,
  onSaved
}: ProductAttributeProps) {
  const { formState, setFormState, setFormRef } = useValidatableForm<ProductAttributeFormState>(
    makeProductAttributeFormState
  )
  const {
    data,
    loading: loadingProductAttribute,
    error: errorLoadingProductAttribute
  } = useFetchSingleProductAttribute({ id: attributeId })

  const [ saveProductAttribute, { loading: loadingSaveProductAttribute } ] =
    useMutateSaveProductAttribute()

  useEffect(() => {
    if (!loadingProductAttribute && data) {
      setFormState((state) => mergeLeft(state, data.singleProductAttribute))
    }
  }, [ data, loadingProductAttribute, setFormState ])

  const handleFieldChange = commonFieldChange(setFormState)

  const handleFormChange = useCallback(
    <T extends keyof ProductAttributeFormState>(
      formData: Record<T, ProductAttributeFormState[T]>
    ) => {
      setFormState((state) => mergeLeft(state, formData))
    },
    [ setFormState ]
  )

  const handleFormSubmit = useCallback(
    (isOk = false) => {
      if (isOk) {
        saveProductAttribute({
          variables: {
            productAttributeInfo: mapStateToApiVariable(formState)
          }
        })
          .then(({ data }) => {
            notifyOk('Группа сохранена')
            setFormState((state) =>
              mergeLeftOnly([ 'id' ], state, data?.productAttribute)
            )

            onSaved?.()
          })
          .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
      }
      else {
        notifyError('Форма заполнена некорректно')
      }
    },
    [ formState, onSaved, saveProductAttribute, setFormState ]
  )

  if (errorLoadingProductAttribute) {
    return (
      <GraphQLCommonErrorDisplay
        title={`Не удалось загрузить атрибут #${attributeId} для редактирования`}
        error={errorLoadingProductAttribute}
      />
    )
  }

  return (
    <Form
      formValue={formState}
      onChange={handleFormChange}
      onSubmit={handleFormSubmit}
      ref={setFormRef}
      model={productAttributeFormValidationModel}
      fluid
    >
      <Panel>
        <Grid fluid>
          <Row>
            <Col md={24}>
              <Form.Group>
                <Form.ControlLabel>Название</Form.ControlLabel>
                <Form.Control placeholder="Название" name="name" />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>Тип группы</Form.ControlLabel>
                <ProductAttributeTypePicker
                  value={formState.value_type}
                  onChange={handleFieldChange('value_type')}
                />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>URL-префикс</Form.ControlLabel>
                <Form.Control placeholder="Url-префикс" name="slug" />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>Префикс</Form.ControlLabel>
                <Form.Control placeholder='Префикс' name='prefix' />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>Постфикс</Form.ControlLabel>
                <Form.Control placeholder='Постфикс' name='postfix' />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>Название в учетной системе</Form.ControlLabel>
                <Form.Control placeholder='Название в учетной системе' name='xname' />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>Участвует в поиске</Form.ControlLabel>
                <FormCheckbox.Control name='is_searchable' />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>Доступен в фильтрах</Form.ControlLabel>
                <FormCheckbox.Control name='is_filterable' />
              </Form.Group>
              { formState.is_filterable && (
                <Form.Group>
                  <Form.ControlLabel>Название в фильтре</Form.ControlLabel>
                  <Form.Control placeholder='Название в фильтре' name='filter_name' />
                </Form.Group>
              )}
            </Col>
          </Row>
        </Grid>
      </Panel>

      <SaveButton
        loading={loadingProductAttribute || loadingSaveProductAttribute}
        disabled={loadingProductAttribute || loadingSaveProductAttribute}
      >
        Cохранить
      </SaveButton>
    </Form>
  )
}
